import { PaymentType } from '@/lib/enums/PaymentType'

export const SAVED_PAYMENT_METHOD_AUTHORIZATION_COPY =
  "I confirm the customer authorized us to save this card and charge it later for this customer's approved orders, balances, or subscriptions."

// Stripe reports a Link payment that a card didn't fund (bank, stablecoin, Pix/UPI, ...) as a card with
// the placeholder brand `link`, last4 `0000`, and expiry 12/2040, or as Link's own payment method type,
// so show it as "Link" without those values. Cards saved in Link keep their card label.
export function isNonCardLink(card) {
  return (
    card?.type === 'link' ||
    String(card?.brand ?? '')
      .trim()
      .toLowerCase() === 'link'
  )
}

export function formatCardBrand(card) {
  if (isNonCardLink(card)) {
    return 'Link'
  }

  const brand = card?.display_brand ?? card?.brand ?? 'Card'
  const normalizedBrand = String(brand).trim()

  if (normalizedBrand.toLowerCase() === 'visa') {
    return 'VISA'
  }

  return normalizedBrand.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function formatCardLast4(card) {
  return isNonCardLink(card) ? '' : String(card?.last4 ?? '').trim()
}

export function formatCardExpiry(card) {
  if (isNonCardLink(card)) {
    return null
  }

  const month = Number(card?.exp_month ?? 0)
  const year = Number(card?.exp_year ?? 0)

  if (!month || !year) {
    return null
  }

  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`
}

export function formatStripePaymentMethodType(paymentMethod) {
  const type = paymentMethod?.type
  if (!type) return null
  if (type === 'afterpay_clearpay') return 'Afterpay / Clearpay'
  if (type === 'card_present') return 'Card'
  return type.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function stripeCard(paymentMethod) {
  const payload = paymentMethod && typeof paymentMethod === 'object' ? paymentMethod : {}
  // Online payments keep details under `card`; terminal (tap/insert) payments under `card_present`.
  return payload.card ?? payload.card_present ?? payload
}

function stripeCardDetails(paymentMethod) {
  const card = stripeCard(paymentMethod)
  const brand = String(card?.display_brand ?? card?.brand ?? '').trim()

  return {
    brand: brand ? formatCardBrand(card) : '',
    last4: formatCardLast4(card),
  }
}

// `short` gives compact receipt labels such as "VISA x4242".
export function getPaymentMethodLabel(payment, { short = false } = {}) {
  if (!payment) return 'Manual payment'
  if (payment.type === PaymentType.Cash.Value) return 'Cash'
  if (payment.type === PaymentType.Check.Value) {
    return payment.reference ? `Check ${payment.reference}` : 'Check'
  }

  const card = stripeCardDetails(payment.stripe_payment_method)
  if (card.last4) {
    if (short) return `${card.brand || 'Card'} x${card.last4}`
    return card.brand ? `${card.brand} ending in ${card.last4}` : `Card ending in ${card.last4}`
  }

  return card.brand || formatStripePaymentMethodType(payment.stripe_payment_method) || 'Card'
}

export function getPaymentTypeBadge(payment) {
  if (!payment) return 'Cash/Check'
  const card = stripeCard(payment.stripe_payment_method)
  if (isNonCardLink(card)) return formatCardBrand(card)
  return [PaymentType.Cash.Value, PaymentType.Check.Value].includes(payment.type)
    ? 'Cash/Check'
    : formatStripePaymentMethodType(payment.stripe_payment_method) || 'Card'
}
